import { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { firebase } from "../firebase";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Icon from "@material-ui/core/Icon";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Collapse from "@material-ui/core/Collapse";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import upperCase from "lodash/upperCase";
import * as routes from "../constants/routes";
import isAdmin from "../utils/isAdmin";
import isSafeAdmin from "../utils/isSafeAdmin";
import AccountContext from "../contexts/AccountContext";

const useStyles = makeStyles((theme) => ({
  userBox: {
    padding: theme.spacing(5),
    minWidth: 300,
    display: "flex",
    flexDirection: "column",
    background: `radial-gradient(circle at 70% 30%, ${theme.palette.primary.light}22, ${theme.palette.background.paper} 70%)`,
  },
  avatar: {
    width: 90,
    height: 90,
    marginBottom: theme.spacing(1),
  },
  avatarNoPhoto: {
    width: 90,
    height: 90,
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(2),
    color: "#fff",
    fontWeight: 900,
    backgroundColor: theme.palette.secondary.main,
    fontSize: "3rem",
  },
  menuButton: {
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(1.5),
  },
  email: {
    fontSize: ".85rem",
  },
  list: {
    padding: 0,
  },
  sectionHeader: {
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sectionTitle: {
    fontWeight: 500,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    opacity: 0.4,
  },
  // Common styles for all menu items to ensure alignment
  menuItem: {
    paddingLeft: theme.spacing(2),
  },
  // Special styling for nested items that preserves icon alignment
  nestedItem: {
    paddingLeft: theme.spacing(2), // Same as regular items
  },
  // Style for the icon container to ensure alignment
  iconContainer: {
    minWidth: 56, // Fixed width for icon container
    display: "flex",
    justifyContent: "flex-start",
  },
  // Style for nested item text to create indentation effect
  nestedText: {
    paddingLeft: theme.spacing(2), // Add indentation to text only
  },
  // Add spacing at the bottom of the user section
  userSectionBottom: {
    paddingBottom: theme.spacing(1),
  },
}));

const UserMenu = ({ user, account }) => {
  const [open, setOpen] = useState(false);
  const [helpSiteDomain, setHelpSiteDomain] = useState("");
  const [userSectionOpen, setUserSectionOpen] = useState(false);
  const [adminSectionOpen, setAdminSectionOpen] = useState(true); // Admin section expanded by default

  const isAdminUser = isAdmin() || isSafeAdmin();

  const history = useHistory();
  const classes = useStyles();

  useEffect(() => {
    if (account?.helpSiteDomain) {
      setHelpSiteDomain(account.helpSiteDomain);
    }
  }, [account]);

  if (!user) return null;

  const { email, displayName, photoURL } = user;

  const handleNavigate = (event) => {
    history.push(event.currentTarget.getAttribute("name"));
    setOpen(false);
  };

  const goToHelpSite = () => {
    window.open(`https://${helpSiteDomain}`);
    setOpen(false);
  };

  const toggleUserSection = () => {
    setUserSectionOpen(!userSectionOpen);
  };

  const toggleAdminSection = () => {
    setAdminSectionOpen(!adminSectionOpen);
  };

  // Custom ListItem component for nested items that maintains icon alignment
  const NestedListItem = ({ icon, text, ...props }) => (
    <ListItem className={classes.nestedItem} {...props}>
      <div className={classes.iconContainer}>
        <ListItemIcon>
          <Icon>{icon}</Icon>
        </ListItemIcon>
      </div>
      <ListItemText primary={text} className={classes.nestedText} />
    </ListItem>
  );

  // Regular menu item with consistent alignment
  const MenuItem = ({ icon, text, ...props }) => (
    <ListItem className={classes.menuItem} {...props}>
      <ListItemIcon>
        <Icon>{icon}</Icon>
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );

  // User menu items including Help
  const userMenuItems = (
    <>
      <NestedListItem
        button
        name={routes.ALBUMS}
        onClick={handleNavigate}
        icon="album"
        text="Tu música"
      />

      <NestedListItem
        button
        name={routes.UPLOAD}
        onClick={handleNavigate}
        icon="publish"
        text="Subir música"
      />

      <NestedListItem
        button
        name={routes.ROYALTIES}
        onClick={handleNavigate}
        icon="attach_money"
        text="Ingresos"
      />

      <NestedListItem
        button
        name={routes.ACCOUNT}
        onClick={handleNavigate}
        icon="account_circle"
        text="Tu cuenta"
      />

      {helpSiteDomain && (
        <NestedListItem
          button
          onClick={goToHelpSite}
          icon="help"
          text="Ayuda"
        />
      )}
    </>
  );

  return (
    <div>
      <IconButton
        className={classes.menuButton}
        color="inherit"
        aria-label="Menu"
        onClick={() => setOpen(true)}
      >
        <MenuIcon />
      </IconButton>

      <Drawer open={open} onClose={() => setOpen(false)}>
        <List component="nav" className={classes.list}>
          <ListItem
            button
            name={routes.ACCOUNT}
            className={classes.userBox}
            onClick={handleNavigate}
          >
            {photoURL ? (
              <Avatar src={photoURL} className={classes.avatar} />
            ) : (
              <Avatar className={classes.avatarNoPhoto}>
                {upperCase(
                  displayName ? displayName.charAt(0) : email.charAt(0)
                )}
              </Avatar>
            )}
            <Typography variant="h6">{displayName || email}</Typography>
            <Typography className={classes.email}>{email}</Typography>
          </ListItem>

          <Divider />

          {isAdminUser ? (
            // For admin users, show collapsible user section
            <>
              <div
                className={classes.sectionHeader}
                onClick={toggleUserSection}
              >
                <Typography className={classes.sectionTitle}>
                  Usuario
                </Typography>
                {userSectionOpen ? (
                  <ExpandLess color="disabled" />
                ) : (
                  <ExpandMore color="disabled" />
                )}
              </div>

              <Collapse in={userSectionOpen} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  className={classes.userSectionBottom}
                >
                  {userMenuItems}
                </List>
              </Collapse>

              <Divider />

              {/* Admin section - now collapsible */}
              <div
                className={classes.sectionHeader}
                onClick={toggleAdminSection}
              >
                <Typography className={classes.sectionTitle}>
                  Administración
                </Typography>
                {adminSectionOpen ? (
                  <ExpandLess color="disabled" />
                ) : (
                  <ExpandMore color="disabled" />
                )}
              </div>

              <Collapse in={adminSectionOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <MenuItem
                    button
                    name={routes.ADMIN_ALBUMS}
                    onClick={handleNavigate}
                    icon="library_music"
                    text="Admin catalog"
                  />

                  <MenuItem
                    button
                    name={routes.MANAGE_USERS}
                    onClick={handleNavigate}
                    icon="lock"
                    text="Admin users"
                  />

                  <MenuItem
                    button
                    name={routes.MANAGE_ACCOUNTS}
                    onClick={handleNavigate}
                    icon="business"
                    text="Admin accounts"
                  />

                  <MenuItem
                    button
                    name={routes.MANAGE_ROYALTIES}
                    onClick={handleNavigate}
                    icon="attach_money"
                    text="Admin ingresos"
                  />

                  <MenuItem
                    button
                    name={routes.DROPS}
                    onClick={handleNavigate}
                    icon="cloud_download"
                    text="Drops"
                  />

                  <MenuItem
                    button
                    name={routes.MANAGE_ARTISTS}
                    onClick={handleNavigate}
                    icon="color_lens"
                    text="Admin artists"
                  />

                  <MenuItem
                    button
                    name={routes.BLACKLIST}
                    onClick={handleNavigate}
                    icon="block"
                    text="Blacklist"
                  />
                </List>
              </Collapse>
            </>
          ) : (
            // For regular users, show normal menu
            <>
              <MenuItem
                button
                name={routes.ALBUMS}
                onClick={handleNavigate}
                icon="album"
                text="Tu música"
              />

              <MenuItem
                button
                name={routes.UPLOAD}
                onClick={handleNavigate}
                icon="publish"
                text="Subir música"
              />

              <MenuItem
                button
                name={routes.ROYALTIES}
                onClick={handleNavigate}
                icon="attach_money"
                text="Ingresos"
              />

              <MenuItem
                button
                name={routes.ACCOUNT}
                onClick={handleNavigate}
                icon="account_circle"
                text="Tu cuenta"
              />

              {helpSiteDomain && (
                <MenuItem
                  button
                  onClick={goToHelpSite}
                  icon="help"
                  text="Ayuda"
                />
              )}
            </>
          )}
        </List>
      </Drawer>
    </div>
  );
};

export default UserMenu;
